import React from "react";
import {
  Drawer,
  Grid,
  Typography,
  Divider,
  Stack,
  IconButton,
  Box,
  Chip,
} from "@mui/material";
import { CloseIcon } from "../../helper/Icons";
import { Button, Input } from "../../components/Common";
import CommonInput from "../../components/Common/Input";
import CommonSelect from "../../components/Common/Select";
import moment from "moment";

const FilterSubmitform = ({
  openDrawer,
  formik,
  value,
  setValue,
  toggleDrawer,
  auditordata,
  formdata,
  onClear,
}) => {
  const drawerWidth = 310;

  const handleApply = () => {
    formik.handleSubmit();
    if (toggleDrawer) toggleDrawer(false);
  };

  const handleReset = () => {
    if (setValue) setValue([]);
    if (onClear) {
      onClear();
    } else {
      formik.resetForm();
      formik.handleSubmit();
    }
    if (toggleDrawer) toggleDrawer(false);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => toggleDrawer && toggleDrawer(false)}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            padding: 2.5,
          },
        }}
      >
        <Box height={"100%"} display="flex" flexDirection="column">
          <Grid container>
            <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between">
              <Typography fontWeight={600} fontSize={"18px"}>
                {"Filter Submissions"}
              </Typography>
              <IconButton
                aria-label="close"
                onClick={() => toggleDrawer && toggleDrawer(false)}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon sx={{ width: "20px", height: "20px" }} />
              </IconButton>
            </Grid>
            <Grid item xs={12} marginY={"8px"}>
              <Divider sx={{ borderColor: "gray.main" }} />
            </Grid>

            <Grid item xs={12} marginY={1}>
              <Grid container gap={2.5}>
                {/* Date Presets */}
                <Grid item xs={12}>
                  <Typography fontSize="12px" fontWeight={600} color="text.secondary" mb={1}>
                    Quick Date Range:
                  </Typography>
                  <Stack flexDirection="row" gap={0.8} flexWrap="wrap">
                    <Chip
                      label="This Month"
                      size="small"
                      variant={
                        formik.values.fromSubmitDate === moment().startOf("month").format("YYYY-MM-DD") &&
                        formik.values.toSubmitDate === moment().endOf("month").format("YYYY-MM-DD")
                          ? "filled"
                          : "outlined"
                      }
                      color="primary"
                      clickable
                      onClick={() => {
                        formik.setFieldValue("fromSubmitDate", moment().startOf("month").format("YYYY-MM-DD"));
                        formik.setFieldValue("toSubmitDate", moment().endOf("month").format("YYYY-MM-DD"));
                      }}
                    />
                    <Chip
                      label="Last Month"
                      size="small"
                      variant={
                        formik.values.fromSubmitDate === moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD") &&
                        formik.values.toSubmitDate === moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD")
                          ? "filled"
                          : "outlined"
                      }
                      color="primary"
                      clickable
                      onClick={() => {
                        formik.setFieldValue(
                          "fromSubmitDate",
                          moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD")
                        );
                        formik.setFieldValue(
                          "toSubmitDate",
                          moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD")
                        );
                      }}
                    />
                    <Chip
                      label="All Dates (Clear)"
                      size="small"
                      variant="outlined"
                      color="error"
                      clickable
                      onClick={() => {
                        formik.setFieldValue("fromSubmitDate", "");
                        formik.setFieldValue("toSubmitDate", "");
                      }}
                    />
                  </Stack>
                </Grid>

                {/* From Date */}
                <Grid item xs={12}>
                  <Stack flexDirection="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography fontSize="13px" fontWeight={500}>
                      From Submit Date
                    </Typography>
                    {formik.values.fromSubmitDate && (
                      <Typography
                        fontSize="11px"
                        color="error"
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => formik.setFieldValue("fromSubmitDate", "")}
                      >
                        Clear
                      </Typography>
                    )}
                  </Stack>
                  <CommonInput
                    name="fromSubmitDate"
                    type="date"
                    isDate={true}
                    formik={formik}
                  />
                </Grid>

                {/* To Date */}
                <Grid item xs={12}>
                  <Stack flexDirection="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography fontSize="13px" fontWeight={500}>
                      To Submit Date
                    </Typography>
                    {formik.values.toSubmitDate && (
                      <Typography
                        fontSize="11px"
                        color="error"
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => formik.setFieldValue("toSubmitDate", "")}
                      >
                        Clear
                      </Typography>
                    )}
                  </Stack>
                  <Input
                    name="toSubmitDate"
                    type="date"
                    formik={formik}
                    isDate={true}
                  />
                </Grid>

                {/* Form Selection */}
                <Grid item xs={12}>
                  <Stack flexDirection="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography fontSize="13px" fontWeight={500}>
                      Form
                    </Typography>
                    {formik.values.formId && (
                      <Typography
                        fontSize="11px"
                        color="error"
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => formik.setFieldValue("formId", "")}
                      >
                        Clear
                      </Typography>
                    )}
                  </Stack>
                  <CommonSelect
                    name="formId"
                    selectList={formdata || []}
                    formik={formik}
                    selectHead={"All Forms"}
                    onChange={(e) => {
                      const val = e.target.value === "SelectCustomer" ? "" : e.target.value;
                      formik.setFieldValue("formId", val);
                    }}
                    value={formik.values.formId || ""}
                  />
                </Grid>

                {/* Auditor Selection */}
                <Grid item xs={12}>
                  <Stack flexDirection="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography fontSize="13px" fontWeight={500}>
                      Auditor
                    </Typography>
                    {formik.values.auditor && (
                      <Typography
                        fontSize="11px"
                        color="error"
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => formik.setFieldValue("auditor", "")}
                      >
                        Clear
                      </Typography>
                    )}
                  </Stack>
                  <CommonSelect
                    name="auditor"
                    selectList={auditordata || []}
                    formik={formik}
                    selectHead={"All Auditors"}
                    onChange={(e) => {
                      const val = e.target.value === "SelectCustomer" ? "" : e.target.value;
                      formik.setFieldValue("auditor", val);
                    }}
                    value={formik.values.auditor || ""}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box mt="auto" pt={2}>
            <Stack flexDirection={"row"} gap={1.5}>
              <Button
                buttonName={"Reset All"}
                color="white"
                style={{ width: "100%", border: "1px solid #ccc" }}
                size="small"
                onClick={handleReset}
              />
              <Button
                buttonName={"Apply Filter"}
                style={{ width: "100%" }}
                size="small"
                onClick={handleApply}
              />
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default FilterSubmitform;
